import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BellIcon,
  BellAlertIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  PhoneIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ClockIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import axios from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import DropdownMenu from '../components/DropdownMenu';

// The panel's alarm codes are an artifact of the original system, so the
// screen shows what they mean instead: HAL and LAL are the critical high and
// low alarm limits, HWL and LWL the high and low warning limits.
const ALARM_TYPE_LABELS = {
  HAL: 'High Alarm',
  HWL: 'High Warning',
  LWL: 'Low Warning',
  LAL: 'Low Alarm',
  SYS: 'System',
  COMM: 'Communication',
};

const alarmTypeLabel = (type) => ALARM_TYPE_LABELS[type] || type;

// apiBase lets the vendor pages point this at their scoped /vendor/alarms
// endpoint; staff pages leave it empty and hit /alarms as before.
const AlarmHistory = React.memo(({ deviceSerial, apiBase = '', canExport = true }) => {
  const { user } = useAuth();
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAlarms, setTotalAlarms] = useState(0);
  const [filters, setFilters] = useState({
    state: 'all',
    severity: 'all',
    type: 'all',
    dateFrom: '',
    dateTo: '',
    timeFrom: '00:00',
    timeTo: '23:59',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [jumpToPage, setJumpToPage] = useState('');
  const [quickDateFilter, setQuickDateFilter] = useState('all');

  const limit = itemsPerPage;

  const fetchAlarms = useCallback(async () => {
    // Skip fetching if modal is open
    if (selectedAlarm) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page,
        limit,
        serialNumber: deviceSerial,
        sortBy: 'timestamp',
        sortOrder: 'desc'
      });

      // Add filters
      if (filters.state !== 'all') params.append('state', filters.state);
      if (filters.severity !== 'all') params.append('severity', filters.severity);
      if (filters.type !== 'all') params.append('type', filters.type);
      // Only add notification filters if they are not 'all' to avoid empty results
      if (filters.notificationChannel && filters.notificationChannel !== 'all') {
        params.append('notificationChannel', filters.notificationChannel);
      }
      if (filters.notificationStatus && filters.notificationStatus !== 'all') {
        params.append('notificationStatus', filters.notificationStatus);
      }
      if (filters.dateFrom) {
        const dateTimeFrom = filters.dateFrom + 'T' + filters.timeFrom;
        params.append('dateFrom', dateTimeFrom);
      }
      if (filters.dateTo) {
        const dateTimeTo = filters.dateTo + 'T' + filters.timeTo;
        params.append('dateTo', dateTimeTo);
      }
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${apiBase}/alarms?${params}`);
      
      if (response.data.success) {
        setAlarms(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalAlarms(response.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch alarm history:', err);
      setError('Failed to load alarm history');
    } finally {
      setLoading(false);
    }
  }, [page, filters, deviceSerial, selectedAlarm, itemsPerPage]);

  useEffect(() => {
    // Don't fetch alarms if the modal is open
    if (!selectedAlarm) {
      fetchAlarms();
    }
  }, [page, filters, deviceSerial, itemsPerPage]);
  
  // Separate effect for initial load when modal closes
  useEffect(() => {
    // When modal closes, only fetch if we need fresh data
    if (!selectedAlarm && alarms.length === 0) {
      fetchAlarms();
    }
  }, [selectedAlarm]);

  const getAlarmIcon = (type, severity) => {
    if (type === 'alarm_cleared') return <CheckCircleIcon className="w-5 h-5" />;
    if (severity === 'critical') return <BellAlertIcon className="w-5 h-5" />;
    if (severity === 'warning') return <ExclamationTriangleIcon className="w-5 h-5" />;
    return <BellIcon className="w-5 h-5" />;
  };

  const getAlarmColor = (alarm) => {
    if (alarm.alarm.state === 'cleared') return 'bg-green-100 text-green-700 border-green-200';
    if (alarm.alarm.severity === 'critical') return 'bg-red-100 text-red-700 border-red-200';
    if (alarm.alarm.severity === 'warning') return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-blue-100 text-blue-700 border-blue-200';
  };

  const getNotificationIcon = (channel) => {
    switch (channel) {
      case 'email': return <EnvelopeIcon className="w-4 h-4" />;
      case 'sms': return <ChatBubbleLeftIcon className="w-4 h-4" />;
      case 'push': return <BellIcon className="w-4 h-4" />;
      case 'call': return <PhoneIcon className="w-4 h-4" />;
      default: return <BellIcon className="w-4 h-4" />;
    }
  };

  const getNotificationStatus = (status) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return { color: 'text-green-600', icon: <CheckCircleIcon className="w-3 h-3" /> };
      case 'failed':
        return { color: 'text-red-600', icon: <XCircleIcon className="w-3 h-3" /> };
      case 'pending':
        return { color: 'text-yellow-600', icon: <ClockIcon className="w-3 h-3" /> };
      default:
        return { color: 'text-gray-600', icon: null };
    }
  };

  const exportAlarms = async (exportFormat = 'json') => {
    try {
      // Fetch all alarms without pagination for export
      const params = new URLSearchParams({
        serialNumber: deviceSerial,
        sortBy: 'timestamp',
        sortOrder: 'desc',
        limit: 10000 // Get all alarms for export
      });

      // Add current filters
      if (filters.state !== 'all') params.append('state', filters.state);
      if (filters.severity !== 'all') params.append('severity', filters.severity);
      if (filters.type !== 'all') params.append('type', filters.type);
      if (filters.dateFrom) {
        const dateTimeFrom = filters.dateFrom + 'T' + filters.timeFrom;
        params.append('dateFrom', dateTimeFrom);
      }
      if (filters.dateTo) {
        const dateTimeTo = filters.dateTo + 'T' + filters.timeTo;
        params.append('dateTo', dateTimeTo);
      }
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${apiBase}/alarms?${params}`);
      const exportData = response.data.data || [];

      if (exportFormat === 'csv') {
        // Convert to CSV
        const headers = ['Date/Time', 'Type', 'Severity', 'State', 'Message', 'Sensor', 'Value', 'Threshold'];
        const csvContent = [
          headers.join(','),
          ...exportData.map(alarm => [
            format(parseISO(alarm.alarm.triggeredAt), 'yyyy-MM-dd HH:mm:ss'),
            alarmTypeLabel(alarm.alarm.type),
            alarm.alarm.severity,
            alarm.alarm.state,
            `"${(alarm.alarm.message || '').replace(/"/g, '""')}"`,
            alarm.alarm.sensorName || '',
            alarm.alarm.value || '',
            alarm.alarm.threshold || ''
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `alarm_history_${deviceSerial}_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        // Export as JSON
        const dataStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `alarm_history_${deviceSerial}_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.json`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const applyQuickDateFilter = (filterType) => {
    const now = new Date();
    let fromDate = new Date();
    let toDate = new Date();

    switch (filterType) {
      case 'today':
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        fromDate.setDate(fromDate.getDate() - 1);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setDate(toDate.getDate() - 1);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'last7days':
        fromDate.setDate(fromDate.getDate() - 7);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'last30days':
        fromDate.setDate(fromDate.getDate() - 30);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'thisMonth':
        fromDate.setDate(1);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'lastMonth':
        fromDate.setMonth(fromDate.getMonth() - 1);
        fromDate.setDate(1);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setDate(0);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'all':
        setFilters({
          ...filters,
          dateFrom: '',
          dateTo: '',
          timeFrom: '00:00',
          timeTo: '23:59'
        });
        setQuickDateFilter('all');
        return;
      default:
        return;
    }

    setFilters({
      ...filters,
      dateFrom: format(fromDate, 'yyyy-MM-dd'),
      dateTo: format(toDate, 'yyyy-MM-dd'),
      timeFrom: format(fromDate, 'HH:mm'),
      timeTo: format(toDate, 'HH:mm')
    });
    setQuickDateFilter(filterType);
  };

  // Calculate notification statistics - must be before conditional returns
  const notificationStats = useMemo(() => {
    const stats = {
      total: 0,
      sent: 0,
      failed: 0,
      email: 0,
      sms: 0,
      push: 0
    };
    
    alarms.forEach(alarm => {
      if (alarm.notifications?.sent) {
        alarm.notifications.sent.forEach(notif => {
          stats.total++;
          if (notif.status === 'sent' || notif.status === 'delivered') stats.sent++;
          if (notif.status === 'failed') stats.failed++;
          if (notif.channel === 'email') stats.email++;
          if (notif.channel === 'sms') stats.sms++;
          if (notif.channel === 'push') stats.push++;
        });
      }
    });
    
    return stats;
  }, [alarms]);

  const AlarmDetail = ({ alarm, onClose }) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900">Alarm Details</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XCircleIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Alarm Info */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Alarm Information</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Severity:</span>
                <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${
                  alarm.alarm.severity === 'critical' ? 'bg-red-100 text-red-700' :
                  alarm.alarm.severity === 'warning' ? 'bg-orange-100 text-orange-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {alarm.alarm.severity}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">State:</span>
                <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${
                  alarm.alarm.state === 'active' ? 'bg-red-100 text-red-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {alarm.alarm.state}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Message:</span>
                <span className="text-sm font-medium text-gray-900">{alarm.alarm.message}</span>
              </div>
              {alarm.alarm.sensorName && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Sensor:</span>
                  <span className="text-sm font-medium text-gray-900">{alarm.alarm.sensorName}</span>
                </div>
              )}
              {alarm.alarm.value !== undefined && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Value:</span>
                  <span className="text-sm font-medium text-gray-900">{alarm.alarm.value}</span>
                </div>
              )}
              {alarm.alarm.threshold !== undefined && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Threshold:</span>
                  <span className="text-sm font-medium text-gray-900">{alarm.alarm.threshold}</span>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Timeline</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Triggered:</span>
                <span className="text-sm text-gray-900">
                  {format(parseISO(alarm.alarm.triggeredAt), 'PPpp')}
                </span>
              </div>
              {alarm.metadata?.receivedAt && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Received by Cloud:</span>
                  <span className="text-sm text-gray-900">
                    {format(parseISO(alarm.metadata.receivedAt), 'PPpp')}
                  </span>
                </div>
              )}
              {alarm.alarm.clearedAt && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Cleared:</span>
                  <span className="text-sm text-gray-900">
                    {format(parseISO(alarm.alarm.clearedAt), 'PPpp')}
                  </span>
                </div>
              )}
              {alarm.statistics?.duration && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Duration:</span>
                  <span className="text-sm text-gray-900">
                    {Math.round(alarm.statistics.duration / 1000 / 60)} minutes
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Notifications */}
          {alarm.notifications?.sent && alarm.notifications.sent.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Notifications</h4>
              <div className="space-y-2">
                {alarm.notifications.sent.map((notif, idx) => {
                  const status = getNotificationStatus(notif.status);
                  return (
                    <div key={idx} className="bg-gray-50 rounded-lg p-3 flex items-start gap-3">
                      <div className="p-1.5 bg-white rounded-lg">
                        {getNotificationIcon(notif.channel)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">
                            {notif.channel.charAt(0).toUpperCase() + notif.channel.slice(1)}
                          </span>
                          <div className={`flex items-center gap-1 ${status.color}`}>
                            {status.icon}
                            <span className="text-xs">{notif.status}</span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-600">
                          To: {notif.recipient}
                        </div>
                        <div className="text-xs text-gray-500">
                          {format(parseISO(notif.sentAt), 'PPp')}
                        </div>
                        {notif.error && (
                          <div className="text-xs text-red-600 mt-1">
                            Error: {notif.error}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Device Information</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Device ID:</span>
                <span className="text-sm font-mono text-gray-900">{alarm.deviceId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Serial Number:</span>
                <span className="text-sm font-mono text-gray-900">{alarm.serialNumber}</span>
              </div>
              {alarm.model && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Model:</span>
                  <span className="text-sm text-gray-900">{alarm.model}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  if (loading && alarms.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading alarm history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchAlarms}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-blue-50 rounded-lg">
              <BellAlertIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Alarm History</h2>
              <p className="text-sm text-gray-600">
                Showing {totalAlarms > 0 ? ((page - 1) * limit) + 1 : 0}-{Math.min(page * limit, totalAlarms)} of {totalAlarms} {totalAlarms === 1 ? 'alarm' : 'alarms'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                showFilters ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FunnelIcon className="w-4 h-4" />
              Filters
              {Object.entries(filters).some(([k, v]) => {
                if (k === 'timeFrom' || k === 'timeTo') return false;
                return v && v !== 'all' && v !== '00:00' && v !== '23:59';
              }) && (
                <span className="w-2 h-2 bg-blue-600 rounded-full" />
              )}
            </button>
            {canExport && (
              <>
                <div className="h-6 w-px bg-gray-200" />
                <DropdownMenu
                  trigger={({ toggle }) => (
                    <button
                      onClick={toggle}
                      className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                      title="Export alarms"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    </button>
                  )}
                >
                  <button
                    onClick={() => exportAlarms('csv')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={() => exportAlarms('json')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Export as JSON
                  </button>
                </DropdownMenu>
              </>
            )}
            <button
              onClick={fetchAlarms}
              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Notification Statistics */}
      {notificationStats.total > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{notificationStats.total}</div>
              <div className="text-xs text-gray-600">Total Notifications</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{notificationStats.sent}</div>
              <div className="text-xs text-gray-600">Sent Successfully</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{notificationStats.failed}</div>
              <div className="text-xs text-gray-600">Failed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{notificationStats.email}</div>
              <div className="text-xs text-gray-600">Email</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{notificationStats.sms}</div>
              <div className="text-xs text-gray-600">SMS/Text</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{notificationStats.push}</div>
              <div className="text-xs text-gray-600">Push</div>
            </div>
          </div>
          {notificationStats.failed > 0 && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-700">
                  {notificationStats.failed} notification{notificationStats.failed !== 1 ? 's' : ''} failed to send. Check alarm details for more information.
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-gray-50 rounded-lg border border-gray-200 p-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Quick Date Filters */}
              <div className="md:col-span-4">
                <div className="flex gap-2 flex-wrap">
                  <label className="text-xs font-medium text-gray-700">Quick filters:</label>
                  <button
                    onClick={() => applyQuickDateFilter('today')}
                    className={`px-2 py-1 text-xs rounded ${quickDateFilter === 'today' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => applyQuickDateFilter('yesterday')}
                    className={`px-2 py-1 text-xs rounded ${quickDateFilter === 'yesterday' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Yesterday
                  </button>
                  <button
                    onClick={() => applyQuickDateFilter('last7days')}
                    className={`px-2 py-1 text-xs rounded ${quickDateFilter === 'last7days' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => applyQuickDateFilter('last30days')}
                    className={`px-2 py-1 text-xs rounded ${quickDateFilter === 'last30days' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Last 30 Days
                  </button>
                  <button
                    onClick={() => applyQuickDateFilter('thisMonth')}
                    className={`px-2 py-1 text-xs rounded ${quickDateFilter === 'thisMonth' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    This Month
                  </button>
                  <button
                    onClick={() => applyQuickDateFilter('lastMonth')}
                    className={`px-2 py-1 text-xs rounded ${quickDateFilter === 'lastMonth' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Last Month
                  </button>
                  <button
                    onClick={() => applyQuickDateFilter('all')}
                    className={`px-2 py-1 text-xs rounded ${quickDateFilter === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    All Time
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="md:col-span-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="Search alarms..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                </div>
              </div>

              {/* State Filter */}
              <div>
                <select
                  value={filters.state}
                  onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="all">All States</option>
                  <option value="active">Active</option>
                  <option value="cleared">Cleared</option>
                  <option value="acknowledged">Acknowledged</option>
                </select>
              </div>

              {/* Severity Filter */}
              <div>
                <select
                  value={filters.severity}
                  onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="all">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info</option>
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="all">All Types</option>
                  <option value="HAL">High Alarm</option>
                  <option value="LAL">Low Alarm</option>
                  <option value="HWL">High Warning</option>
                  <option value="LWL">Low Warning</option>
                  <option value="SYS">System</option>
                  <option value="COMM">Communication</option>
                </select>
              </div>

              {/* Notification Channel Filter */}
              <div>
                <select
                  value={filters.notificationChannel}
                  onChange={(e) => setFilters({ ...filters, notificationChannel: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  title="Filter by notification channel"
                >
                  <option value="all">All Channels</option>
                  <option value="email">📧 Email Only</option>
                  <option value="sms">💬 SMS/Text Only</option>
                  <option value="push">🔔 Push Notifications</option>
                  <option value="none">🔕 No Notifications</option>
                </select>
              </div>

              {/* Notification Status Filter */}
              <div>
                <select
                  value={filters.notificationStatus}
                  onChange={(e) => setFilters({ ...filters, notificationStatus: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  title="Filter by notification status"
                >
                  <option value="all">All Statuses</option>
                  <option value="sent">✅ Sent</option>
                  <option value="delivered">✅ Delivered</option>
                  <option value="failed">❌ Failed</option>
                  <option value="pending">⏳ Pending</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Date & Time Range</label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      placeholder="From Date"
                    />
                    <input
                      type="time"
                      value={filters.timeFrom}
                      onChange={(e) => setFilters({ ...filters, timeFrom: e.target.value })}
                      className="w-24 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      placeholder="To Date"
                    />
                    <input
                      type="time"
                      value={filters.timeTo}
                      onChange={(e) => setFilters({ ...filters, timeTo: e.target.value })}
                      className="w-24 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              <div className="md:col-span-4 flex justify-end">
                <button
                  onClick={() => {
                    setFilters({
                      state: 'all',
                      severity: 'all',
                      type: 'all',
                      dateFrom: '',
                      dateTo: '',
                      timeFrom: '00:00',
                      timeTo: '23:59',
                      search: ''
                    });
                    setQuickDateFilter('all');
                  }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Alarms List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {alarms.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BellIcon className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-900 font-medium">No alarms found</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your filters or check back later</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {alarms.map((alarm, index) => (
              <div
                key={alarm._id}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Icon & Status */}
                  <div className={`p-2.5 rounded-lg ${getAlarmColor(alarm)}`}>
                    {getAlarmIcon(alarm.alarm.type, alarm.alarm.severity)}
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 min-w-0">
                    {/* Top Row */}
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">
                          {alarm.alarm.message}
                        </h4>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                          <span className="flex items-center gap-1">
                            <span className="font-medium">Type:</span> {alarmTypeLabel(alarm.alarm.type)}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <span className="font-medium">Sensor:</span> {alarm.alarm.sensorName || 'N/A'}
                          </span>
                          {alarm.alarm.value !== undefined && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <span className="font-medium">Value:</span> {alarm.alarm.value}
                                {alarm.alarm.threshold !== undefined && ` (Threshold: ${alarm.alarm.threshold})`}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        alarm.alarm.state === 'active' ? 'bg-red-100 text-red-700' :
                        alarm.alarm.state === 'cleared' ? 'bg-green-100 text-green-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {alarm.alarm.state}
                      </span>
                    </div>

                    {/* Bottom Row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <ClockIcon className="w-3.5 h-3.5" />
                          {format(parseISO(alarm.alarm.triggeredAt), 'MMM dd, yyyy HH:mm')}
                        </span>
                        <span>({formatDistanceToNow(parseISO(alarm.alarm.triggeredAt), { addSuffix: true })})</span>
                      </div>

                      <div className="flex items-center gap-3">
                        {/* Notifications */}
                        {alarm.notifications?.sent && alarm.notifications.sent.length > 0 ? (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              {alarm.notifications.sent.map((notif, idx) => {
                                const status = getNotificationStatus(notif.status);
                                return (
                                  <div
                                    key={idx}
                                    className={`relative p-1 rounded ${
                                      notif.status === 'failed' ? 'bg-red-50 border border-red-200' : 
                                      notif.status === 'sent' || notif.status === 'delivered' ? 'bg-green-50 border border-green-200' : 
                                      'bg-yellow-50 border border-yellow-200'
                                    }`}
                                    title={`${notif.channel}: ${notif.status} to ${notif.recipient}`}
                                  >
                                    {getNotificationIcon(notif.channel)}
                                    {notif.status === 'failed' && (
                                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <div className="text-xs text-gray-500">
                              {alarm.notifications.sent.filter(n => n.status === 'failed').length > 0 && (
                                <span className="text-red-600 font-medium">
                                  {alarm.notifications.sent.filter(n => n.status === 'failed').length} failed
                                </span>
                              )}
                              {alarm.notifications.sent.filter(n => n.status === 'sent' || n.status === 'delivered').length > 0 && (
                                <span className="text-green-600 font-medium ml-2">
                                  {alarm.notifications.sent.filter(n => n.status === 'sent' || n.status === 'delivered').length} sent
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">No notifications</div>
                        )}

                        {/* Details Button */}
                        <button
                          onClick={() => {
                            setSelectedAlarm(alarm);
                            setAutoRefreshEnabled(false);
                          }}
                          className="px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="text-xs text-gray-600">
                  Showing {totalAlarms > 0 ? ((page - 1) * limit) + 1 : 0}-{Math.min(page * limit, totalAlarms)} of {totalAlarms} alarms
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600">Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                    className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={jumpToPage}
                    onChange={(e) => setJumpToPage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const pageNum = parseInt(jumpToPage);
                        if (pageNum >= 1 && pageNum <= totalPages) {
                          setPage(pageNum);
                          setJumpToPage('');
                        }
                      }
                    }}
                    placeholder="Page"
                    className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                  <button
                    onClick={() => {
                      const pageNum = parseInt(jumpToPage);
                      if (pageNum >= 1 && pageNum <= totalPages) {
                        setPage(pageNum);
                        setJumpToPage('');
                      }
                    }}
                    className="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                  >
                    Go
                  </button>
                </div>
                <div className="text-xs text-gray-500">of {totalPages} pages</div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-1 mt-3">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className={`p-1.5 rounded ${
                  page === 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (page <= 3) {
                    pageNum = idx + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = page - 2 + idx;
                  }
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => setPage(pageNum)}
                      className={`px-2.5 py-1 rounded text-xs font-medium ${
                        pageNum === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className={`p-1.5 rounded ${
                  page === totalPages
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Alarm Detail Modal */}
      <AnimatePresence>
        {selectedAlarm && (
          <AlarmDetail
            alarm={selectedAlarm}
            onClose={() => {
              setSelectedAlarm(null);
              setAutoRefreshEnabled(true);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
});

AlarmHistory.displayName = 'AlarmHistory';

export default AlarmHistory;